import React from "react";
import {
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    Box
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    criteriaRow: {
        padding: theme.spacing(2),
        borderBottom: '1px solid #e0e0e0',
        '&:last-child': {
            borderBottom: 'none'
        }
    },
    criteriaName: {
        fontWeight: 500
    },
    mandatoryBadge: {
        display: 'inline-block',
        marginLeft: theme.spacing(1),
        padding: '2px 8px',
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
        borderRadius: 4,
        fontSize: '0.75rem',
        fontWeight: 500
    }
}));

interface FuzzyLogicCriteria {
    id: number;
    name: string;
    fieldName: string;
    fieldType: string;
    description: string | null;
    isMandatory?: boolean;
}

interface SelectedCriteria {
    fuzzyLogicSelectionCriteriaId: number;
    isSelected: boolean;
}

interface Props {
    selectedCriteria: SelectedCriteria[];
    availableCriteria: FuzzyLogicCriteria[];
    onChange: (criteria: SelectedCriteria[]) => void;
    disabled?: boolean;
}

export default function FuzzyLogicSelectionCriteriaFieldArray({
    selectedCriteria,
    availableCriteria,
    onChange,
    disabled = false
}: Props) {
    const classes = useStyles();

    const handleToggleChange = (criteriaId: number, checked: boolean) => {
        const existingIndex = selectedCriteria.findIndex(c => c.fuzzyLogicSelectionCriteriaId === criteriaId);
        
        if (existingIndex >= 0) {
            // Update existing
            const updated = [...selectedCriteria];
            updated[existingIndex] = {
                ...updated[existingIndex],
                isSelected: checked
            };
            onChange(updated);
        } else {
            // Add new
            onChange([
                ...selectedCriteria,
                {
                    fuzzyLogicSelectionCriteriaId: criteriaId,
                    isSelected: checked
                }
            ]);
        }
    };

    const isSelected = (criteriaId: number): boolean => {
        const found = selectedCriteria.find(c => c.fuzzyLogicSelectionCriteriaId === criteriaId);
        return found?.isSelected || false;
    };

    return (
        <Box>
            {availableCriteria.map((criteria) => {
                const isMandatory = criteria?.isMandatory || false;
                const checked = isMandatory || isSelected(criteria.id);
                
                return (
                    <div key={criteria.id} className={classes.criteriaRow}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={checked}
                                    onChange={(e) => handleToggleChange(criteria.id, e.target.checked)}
                                    disabled={disabled || isMandatory}
                                    color="primary"
                                />
                            }
                            label={
                                <Box display="flex" alignItems="center">
                                    <Typography className={classes.criteriaName}>
                                        {criteria.name}
                                    </Typography>
                                    {isMandatory && (
                                        <span className={classes.mandatoryBadge}>Mandatory</span>
                                    )}
                                </Box>
                            }
                        />
                        {criteria?.description && (
                            <Typography variant="caption" color="textSecondary" display="block" style={{ marginLeft: 32 }}>
                                {criteria.description}
                            </Typography>
                        )}
                    </div>
                );
            })}
        </Box>
    );
}
